import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, DataTable, StatusBadge } from "@/components/common";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const empty = {
  municipality_id: "", name: "", domain: "", url: "",
  contact_name: "", contact_email: "", contact_phone: "",
  status: "active", notes: "",
};

export default function PublisherSitesPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [muns, setMuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  async function load() {
    setLoading(true);
    try {
      const [sites, muns] = await Promise.all([
        api.get("/admin/publisher-sites"),
        api.get("/admin/municipalities"),
      ]);
      setRows(sites.data);
      setMuns(muns.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const munMap = useMemo(() => Object.fromEntries(muns.map((m) => [m.id, m.name])), [muns]);
  const filtered = filter === "all" ? rows : rows.filter((r) => r.municipality_id === filter);

  function openNew() { setEditing(null); setForm({ ...empty, municipality_id: muns[0]?.id || "" }); setOpen(true); }
  function openEdit(r) {
    setEditing(r);
    setForm({
      municipality_id: r.municipality_id, name: r.name, domain: r.domain, url: r.url || "",
      contact_name: r.contact_name || "", contact_email: r.contact_email || "",
      contact_phone: r.contact_phone || "", status: r.status || "active", notes: r.notes || "",
    });
    setOpen(true);
  }
  async function save() {
    try {
      if (editing) {
        await api.put(`/admin/publisher-sites/${editing.id}`, form);
        toast.success("Site güncellendi");
      } else {
        await api.post("/admin/publisher-sites", form);
        toast.success("Site eklendi");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function remove(r) {
    if (!window.confirm(`${r.name} silinsin mi?`)) return;
    try { await api.delete(`/admin/publisher-sites/${r.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Yayıncı Ağı"
        title="Yayıncı Siteleri"
        description="Belediyelerin anlaşmalı olduğu tüm yayıncı web siteleri."
        actions={isSuper && (
          <Button className="bg-red-600 hover:bg-red-700 rounded-sm h-10 font-heading font-semibold uppercase text-xs tracking-wider"
            onClick={openNew} data-testid="add-site-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Site
          </Button>
        )}
      />

      {isSuper && (
        <div className="flex items-center gap-3">
          <Label className="text-xs text-slate-600">Belediye filtresi</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-64 h-9" data-testid="site-filter-mun"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
        <DataTable
          testid="sites-table"
          defaultSort={{ key: "site", dir: "asc" }}
          columns={[
            { label: "Site", sortKey: "site", sortValue: (r) => r.name || r.domain || "", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500 font-mono">{r.domain}</div></div> },
            { label: "Alan Adı", sortKey: "domain", sortValue: (r) => r.domain || "", render: (r) => <span className="font-mono text-xs">{r.domain}</span> },
            { label: "Belediye", sortKey: "mun", sortValue: (r) => munMap[r.municipality_id] || "", render: (r) => munMap[r.municipality_id] || "—" },
            { label: "Yerleşim", sortKey: "placements", sortValue: (r) => r.placement_count || 0, render: (r) => r.placement_count || 0 },
            { label: "Durum", sortKey: "status", sortValue: (r) => (r.status === "active" ? "Aktif" : "Pasif"), render: (r) => <StatusBadge status={r.status} /> },
            {
              label: "İşlemler", render: (r) => isSuper && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-site-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-site-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              )
            },
          ]}
          rows={filtered}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Site Düzenle" : "Yeni Yayıncı Sitesi"}</DialogTitle>
            <DialogDescription>Belediyeye bağlı yayıncı sitenin beyaz-liste bilgisini tanımlayın. Sistem reklamları yalnızca burada tanımlı domain&apos;lerde gösterir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Belediye</Label>
              <Select value={form.municipality_id} onValueChange={(v) => setForm({ ...form, municipality_id: v })}>
                <SelectTrigger data-testid="site-form-mun"><SelectValue placeholder="Belediye seçin" /></SelectTrigger>
                <SelectContent>{muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Site Adı</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Alan Adı</Label>
                <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="ornek.com" data-testid="site-form-domain" />
              </div>
              <div>
                <Label>Tam URL</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://ornek.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Yetkili</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div><Label>E-posta</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            </div>
            <div><Label>Notlar</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Pasif</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="site-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
